import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function RatePlans() {
  const [plans, setPlans] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/rate-plans"), api.get("/dynamic-rules")]);
      setPlans(p.data); setRules(r.data.rules || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function toggleRule(rule) {
    try {
      await api.patch(`/dynamic-rules/${rule.id}`, { enabled: !rule.enabled });
      load();
    } catch { toast.error("Failed"); }
  }

  async function togglePlanStatus(p) {
    const next = p.status === "active" ? "draft" : "active";
    try { await api.patch(`/rate-plans/${p.id}`, { status: next }); toast.success(`${p.name} → ${next}`); load(); }
    catch { toast.error("Failed"); }
  }

  async function remove(id) {
    if (!confirm("Delete this rate plan?")) return;
    try { await api.delete(`/rate-plans/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  }

  function formatRate(p) {
    if (p.pricing_type === "dynamic") return "Dynamic";
    if (p.pricing_type === "bar_discount") return `BAR ${p.discount_pct}%`;
    return `₹${(p.fixed_rate || 0).toLocaleString("en-IN")}`;
  }

  return (
    <div className="space-y-8" data-testid="rate-plans-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Revenue</div>
          <h1 className="font-display text-3xl md:text-4xl">Rate Plans</h1>
        </div>
        <button onClick={() => setAdding(true)} className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="new-rate-plan-btn"><Plus className="w-4 h-4" /> New rate plan</button>
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="rate-plans-table">
        {loading && <div className="p-8 text-white/50">Loading…</div>}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                  <th className="px-5 py-4">Rate Plan</th>
                  <th className="px-5 py-4">Room Types</th>
                  <th className="px-5 py-4">Rate</th>
                  <th className="px-5 py-4">Occupancy Rule</th>
                  <th className="px-5 py-4">Meal Plan</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 last:border-none hover:bg-[#1A2235]" data-testid={`plan-row-${p.id}`}>
                    <td className="px-5 py-4 font-medium">{p.name}</td>
                    <td className="px-5 py-4 text-white/70">{(p.room_types || []).join(", ") || "All"}</td>
                    <td className="px-5 py-4 font-display">{formatRate(p)}</td>
                    <td className="px-5 py-4 text-white/70">{p.occupancy_rule || "—"}</td>
                    <td className="px-5 py-4 text-white/70">{p.meal_plan || "Room Only"}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => togglePlanStatus(p)} className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${p.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-white/5 text-white/60 border-white/10"}`}>{p.status}</button>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => remove(p.id)} className="text-xs text-orange-400 hover:text-orange-300 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display text-2xl mb-4">Dynamic Pricing Rules</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {rules.map((r) => (
            <div key={r.id} className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid={`rule-${r.id}`}>
              <div className="font-display text-lg mb-2">{r.label}</div>
              <p className="text-white/55 text-sm leading-relaxed">{r.description}</p>
              <button onClick={() => toggleRule(r)} className={`mt-5 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border ${r.enabled ? "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" : "bg-white/5 text-white/60 border-white/10"}`} data-testid={`rule-toggle-${r.id}`}>
                {r.enabled ? "On" : "Off"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {adding && <NewPlanDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function NewPlanDialog({ onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", room_types: "All", pricing_type: "dynamic",
    discount_pct: "", fixed_rate: "", occupancy_rule: "", meal_plan: "Room Only", status: "draft",
  });
  const [loading, setLoading] = useState(false);

  function upd(k, v) { setForm({ ...form, [k]: v }); }

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        room_types: form.room_types ? form.room_types.split(",").map(s => s.trim()) : [],
        pricing_type: form.pricing_type,
        discount_pct: form.pricing_type === "bar_discount" && form.discount_pct ? Number(form.discount_pct) : null,
        fixed_rate: form.pricing_type === "fixed" && form.fixed_rate ? Number(form.fixed_rate) : null,
        occupancy_rule: form.occupancy_rule || null,
        meal_plan: form.meal_plan,
        status: form.status,
      };
      await api.post("/rate-plans", payload);
      toast.success("Rate plan created");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#12182B] border border-white/10 rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display text-xl">New rate plan</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Plan name"><input required value={form.name} onChange={(e) => upd("name", e.target.value)} className="input" data-testid="plan-name" /></Field>
          <Field label="Room types (comma)"><input value={form.room_types} onChange={(e) => upd("room_types", e.target.value)} className="input" data-testid="plan-types" /></Field>
          <Field label="Pricing"><select value={form.pricing_type} onChange={(e) => upd("pricing_type", e.target.value)} className="input"><option value="dynamic">Dynamic</option><option value="bar_discount">BAR discount</option><option value="fixed">Fixed</option></select></Field>
          {form.pricing_type === "bar_discount" && <Field label="Discount %"><input type="number" value={form.discount_pct} onChange={(e) => upd("discount_pct", e.target.value)} className="input" data-testid="plan-discount" /></Field>}
          {form.pricing_type === "fixed" && <Field label="Fixed rate (₹)"><input type="number" value={form.fixed_rate} onChange={(e) => upd("fixed_rate", e.target.value)} className="input" data-testid="plan-fixed" /></Field>}
          <Field label="Occupancy rule"><input value={form.occupancy_rule} onChange={(e) => upd("occupancy_rule", e.target.value)} className="input" placeholder="e.g. Min 3 nights" /></Field>
          <Field label="Meal plan"><select value={form.meal_plan} onChange={(e) => upd("meal_plan", e.target.value)} className="input">{["Room Only", "Breakfast", "Half Board", "Full Board", "All Inclusive"].map(x => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Status"><select value={form.status} onChange={(e) => upd("status", e.target.value)} className="input"><option value="draft">Draft</option><option value="active">Active</option></select></Field>
        </div>
        <button type="submit" disabled={loading} className="btn-gold w-full rounded-md py-3 mt-6 disabled:opacity-60" data-testid="plan-submit">{loading ? "Creating…" : "Create plan"}</button>
        <style>{`.input { width: 100%; background: #0a0f1c; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px 12px; color: #F8F9FA; font-size: 14px; outline: none; } .input:focus { border-color: rgba(212, 175, 55, 0.6); }`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </div>
  );
}
